// Copyright (c) StrangeBee 2023

import React, { ReactElement, useContext, useMemo, useState } from 'react';
import RouteBuilder from '@Services/RouteBuilder';
import { ICONS } from '@Constants/iconsApp';
import { THTooltip } from '@Molecules/THTooltip/THTooltip';
import { Link } from 'react-router-dom';
import { useLocalMessage } from '@Helpers/locale';
import { RouteType } from '@Types';
import { DashboardInput, DashboardOutput, ObjectTypeV1 } from 'thehive-sdk';
import client from '@Client';
import useConfigList from '@Hooks/useConfigList';
import { THList } from '@Molecules/THList';
import DashboardImport from '@Organisms/Dashboard/DashboardImport/DashboardImport';
import { computeEventList } from '@Organisms/Dashboard/eventList';
import { handleNotificationError, openMessage } from '@Helpers/notification';
import { DashboardManage } from '@Organisms/Dashboard/DashboardDrawer/DashboardManage';
import AddButton from '@Atoms/Buttons/AddButton/AddButton';
import THTag from '@Atoms/THTag/THTag';
import { HTMLColors } from '@Constants/colors';
import { ManageSwitch } from '@Molecules/ManageSwitch/ManageSwitch';
import { THFilter } from '@Molecules/THFilter/THFilter';
import { useListAllFields } from '@Queries/describeQueries';
import { Footer } from '@Organisms/ExtendFilterFooter/ExtendFilterFooter';
import { StorageFilterEnum } from '@Hooks/useStoragePage';
import { FormatMultipleDate } from '@Molecules/Format';
import { ActionsList } from '@Atoms/ActionsList/ActionsList';
import { actionList } from '@Organisms/Dashboard/actionList';
import { THButton } from '@Molecules/Inputs/THButton/THButton';
import { convertOldDashboard } from '@Helpers/dashboard';
import { ManageViewList } from '@Molecules/ManageViewList/ManageViewList';
import { StatusContext } from '@Context/StatusProvider';
import { DisplayEmptyData } from '@Atoms/Display/DisplayEmptyData/DisplayEmptyData';
import AssigneeInfo from '@Molecules/AssigneeInfo/AssigneeInfo';
import { ColumnType } from 'antd/lib/table';
import usePageTitle from '@Hooks/usePageTitle';

import './DashboardList.scss';

const DashboardList = (): ReactElement => {
    const i = useLocalMessage();

    const { quotas } = useContext(StatusContext);

    const config = useConfigList<DashboardOutput>({
        controller: client.DashboardController,
        selector: {
            head: [{ name: 'listDashboard' }],
            name: 'get-all-dashboards',
            sort: ['+status', '+title'],
        },
        objectType: ObjectTypeV1.DASHBOARD,
        storageId: StorageFilterEnum.DASHBOARD,
    });

    const {
        dataSource = [],
        countRefetch,
        listRefetch,
        filterManager,
        columnsRenderer,
        storagePageManager,
        storageViewManager,
        status,
    } = config;

    const [localListAsGroup, setLocalListAsGroup] = useState(true);
    const eventList = useMemo(
        () =>
            computeEventList({
                controller: client.DashboardController,
                locale: i,
                onSuccess: (count, message) => {
                    count && countRefetch();
                    message && openMessage('success', message);
                    listRefetch();
                },
                onError: handleNotificationError,
            }),
        [listRefetch, countRefetch, i],
    );

    const dashboardList = useMemo(
        () =>
            localListAsGroup
                ? dataSource.reduce((acc, curr) => {
                      const currentGroup = acc[curr.group] || [];

                      return { ...acc, [curr.group]: [...currentGroup, curr] };
                  }, {} as { [key: string]: DashboardOutput[] })
                : dataSource,
        [localListAsGroup, dataSource],
    );

    const getColumns = (disableSort = false): ColumnType<DashboardOutput>[] =>
        columnsRenderer([
            {
                title: [{ label: i('common.status'), sortField: 'status', disableSort }],
                className: 'dashboard-list__column__status',
                render: ({ status }, { custom: filterCustom }) => (
                    <THTag
                        value={status}
                        full
                        color={HTMLColors.BLUE}
                        onClick={() => filterCustom?.({ field: 'status', operator: 'any', values: [status] })}
                        outline
                    />
                ),
            },
            {
                title: [{ label: i('common.name'), sortField: 'title', disableSort }],
                className: 'dashboard-list__column__name column_preview',
                render: (dashboard) => (
                    <>
                        {dashboard.version === 1 ? (
                            <Link
                                to={RouteBuilder.compute('dashboards.details', { dashboardId: dashboard._id })}
                                className="dashboard-list__name"
                            >
                                {dashboard.title}
                            </Link>
                        ) : (
                            <div className="dashboard-title__old-version">
                                <THTooltip
                                    title="This dashboard needs to be converted before you can access it!"
                                    placement="bottomRight"
                                >
                                    <ICONS.WarningOutlined className="icon" />
                                </THTooltip>
                                {dashboard.title}
                            </div>
                        )}
                    </>
                ),
            },
            {
                title: i('common.version'),
                className: 'dashboard-list__column__version',
                render: ({ version }, { custom: filterCustom }) => (
                    <THButton
                        type="link"
                        onClick={() => filterCustom({ field: 'version', operator: 'eq', values: [version] })}
                    >
                        {version}
                    </THButton>
                ),
            },
            {
                title: `# ${i('common.widget')}`,
                className: 'dashboard-list__column__widget',
                render: ({ definition }) => {
                    const count = definition?.items?.reduce((acc: number, item: any) => (acc += item.items.length), 0);
                    return <>{count}</>;
                },
            },
            {
                title: i('common.owner'),
                className: 'dashboard-list__column__owner',
                render: ({ owner }) => <AssigneeInfo assignee={owner} avatarOnly />,
            },
            {
                title: [
                    { label: i('common.dates') },
                    { label: 'C.', sortField: '_createdAt', disableSort },
                    { label: 'U.', sortField: '_updatedAt', disableSort },
                ],
                className: 'dashboard-list__column__dates',
                render: ({ _createdAt, _updatedAt }, { date: filterDate }): ReactElement => (
                    <FormatMultipleDate
                        dates={[
                            {
                                label: 'C.',
                                value: _createdAt,
                                onClick: (): void => filterDate(_createdAt, '_createdAt'),
                            },
                            {
                                label: 'U.',
                                value: _updatedAt,
                                onClick: (): void => filterDate(_updatedAt, '_updatedAt'),
                            },
                        ]}
                    />
                ),
            },
            {
                className: 'dashboard-list__column__actions',
                render: (dashboard) => (
                    <ActionsList
                        actionList={actionList({ locale: i, eventList, describeData })([dashboard], () => {
                            // test
                        })}
                        scope="single"
                        isIcon
                    />
                ),
            },
        ]);

    const { data: describeData } = useListAllFields();

    usePageTitle(i('page-title.dashboards.list'));

    return (
        <THList>
            <THList.Header>
                <THList.Slot name="MANAGE">
                    <DashboardManage
                        onFinish={(values) => eventList.onCreate(values)}
                        triggerElem={
                            <AddButton label={i('dashboard.create.title')} disabled={!quotas?.dashboards?.valid} />
                        }
                    />

                    <DashboardImport
                        triggerElem={
                            <THButton type="default" disabled={!quotas?.dashboards?.valid}>
                                {i('dashboard.import.button')}
                            </THButton>
                        }
                        onImport={(dashboard) => {
                            const converted =
                                dashboard.version !== 1
                                    ? (convertOldDashboard(dashboard, describeData) as DashboardInput)
                                    : dashboard;

                            eventList.onCreate({ ...converted, status: dashboard.status });
                        }}
                    />

                    {storagePageManager && storageViewManager ? (
                        <ManageViewList
                            pageManager={storagePageManager}
                            viewManager={storageViewManager}
                            beforeCreate={(content: any): any => {
                                filterManager.applyFilters();
                                return { ...content, filters: filterManager.tempFilters };
                            }}
                        />
                    ) : null}
                </THList.Slot>

                <THList.Slot name="NAVIGATION">
                    <ManageSwitch
                        label={i('dashboard.list.table.group.show-group')}
                        value={localListAsGroup}
                        onChange={(): void => {
                            setLocalListAsGroup(!localListAsGroup || false);
                        }}
                        icon={<ICONS.GroupOutlined />}
                    />
                    <ManageSwitch
                        label={i('common.filters')}
                        value={filterManager.editFilters}
                        onChange={(editFilters): void => {
                            filterManager.setEditFilters(editFilters);
                        }}
                        icon={<ICONS.FilterOutlined />}
                    />
                </THList.Slot>

                <THList.Slot name="CONTENT">
                    {(filterManager.editFilters || !!filterManager.tempFilters.length) && (
                        <THFilter filterConfig={filterManager} describeField={describeData?.[ObjectTypeV1.DASHBOARD]}>
                            <Footer filterConfig={filterManager} />
                        </THFilter>
                    )}
                </THList.Slot>
            </THList.Header>

            {Array.isArray(dashboardList) ? (
                <THList.Content
                    dataSource={dashboardList}
                    columns={getColumns()}
                    rowKey="_id"
                    emptyPlaceholder={<DisplayEmptyData spin={status === 'loading'} label={i('task.none')} />}
                />
            ) : (
                <div className="task-list__group-content">
                    {Object.entries(dashboardList).map(
                        ([name, data]): ReactElement => (
                            <THList.Content
                                key={name}
                                name={name}
                                dataSource={data}
                                columns={getColumns(true)}
                                rowKey="_id"
                                emptyPlaceholder={
                                    <DisplayEmptyData spin={status === 'loading'} label={i('dashboard.none')}>
                                        <DashboardManage
                                            onFinish={(values) => eventList.onCreate(values)}
                                            triggerElem={
                                                <THButton type="link" disabled={!quotas?.dashboards?.valid}>
                                                    {i('dashboard.none-add')}
                                                </THButton>
                                            }
                                        />

                                        {` ${i('common.or')} `}

                                        <DashboardImport
                                            triggerElem={
                                                <THButton type="link" disabled={!quotas?.dashboards?.valid}>
                                                    {i('dashboard.none-import')}
                                                </THButton>
                                            }
                                            onImport={(dashboard) => {
                                                const converted =
                                                    dashboard.version !== 1
                                                        ? (convertOldDashboard(
                                                              dashboard,
                                                              describeData,
                                                          ) as DashboardInput)
                                                        : dashboard;
                                                eventList.onCreate({ ...converted, status: dashboard.status });
                                            }}
                                        />
                                    </DisplayEmptyData>
                                }
                            />
                        ),
                    )}
                </div>
            )}
        </THList>
    );
};

const path = RouteBuilder.compute('dashboards.default');

const MenuLink = (): ReactElement => {
    const local = useLocalMessage();
    return <Link to={path}>{local('mainLayout.userMenu.dashboards')}</Link>;
};

export const Route: RouteType = {
    path,
    menu: {
        key: 'dashboards',
        icon: <ICONS.DashboardIcon />,
        render: <MenuLink />,
        selectTab: path,
        group: 'second-space',
    },
    breadcrumb: { label: 'breadcrumb.global.dashboards' },
    content: <DashboardList />,
    space: ['user'],
    exact: true,
};
